"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Badge } from "../ui/badge"
import { UserCheck, Loader2 } from "lucide-react"
import { apiClient } from "../lib/apiClient"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"

interface ManagedUser {
  userID: number
  username: string
  isPrimary: boolean
  isSecondary: boolean
}

interface ManagedUsersModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ManagedUsersModal({ isOpen, onClose }: ManagedUsersModalProps) {
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchManagedUsers()
    } else {
      // Reset state when modal closes
      setUsers([])
      setError(null)
    }
  }, [isOpen])

  const fetchManagedUsers = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get("/Dashboard/getmanagedusers")
      if (response.data && Array.isArray(response.data)) {
        setUsers(response.data)
      } else {
        setUsers([])
      }
    } catch (err: any) {
      console.error("Error fetching managed users:", err)
      setError("Failed to load managed users. Please try again.")
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col max-h-[80vh] p-0">
        {/* Fixed Header */}
        <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-green-600" />
            Managed Users
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading users...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-sm">No managed users found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 border-b border-gray-200">
                    <TableHead className="w-12 font-semibold text-gray-700 p-4">#</TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4">User Name</TableHead>
                    <TableHead className="font-semibold text-gray-700 p-4 text-center">Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow
                      key={user.userID}
                      className="hover:bg-gray-50 transition-colors border-b border-gray-100"
                    >
                      <TableCell className="font-medium text-gray-600 p-4">{index + 1}</TableCell>
                      <TableCell className="font-medium text-gray-900 p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-semibold text-gray-600">
                              {getInitials(user.username)}
                            </span>
                          </div>
                          <span>{user.username}</span>
                        </div>
                      </TableCell>
                      <TableCell className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          {user.isPrimary && (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">
                              Primary
                            </Badge>
                          )}
                          {user.isSecondary && (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
                              Secondary
                            </Badge>
                          )}
                          {!user.isPrimary && !user.isSecondary && (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-end pt-4 pb-6 px-6 border-t border-gray-200 flex-shrink-0">
          <div className="text-sm text-gray-600">
            Total: <span className="font-semibold">{users.length}</span> user{users.length !== 1 ? "s" : ""}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

